import React from 'react';
import CalendarHeatmap from 'react-calendar-heatmap';
import { Tooltip } from 'react-tooltip';
import { useTheme } from '../../contexts/ThemeContext';
import 'react-calendar-heatmap/dist/styles.css';

interface DailyActivity {
  date: string;
  minutes: number;
}

interface ReadingActivityHeatmapProps {
  activity: DailyActivity[];
  loading?: boolean;
}

export default function ReadingActivityHeatmap({ activity, loading }: ReadingActivityHeatmapProps) {
  const { isDarkMode } = useTheme();

  // Calculate date range (past 365 days)
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 365);

  // Convert activity to heatmap format
  const activityMap = new Map(activity.map(a => [a.date, a.minutes]));

  const getColorLevel = (minutes: number): number => {
    if (minutes === 0) return 0;
    if (minutes <= 15) return 1;
    if (minutes <= 45) return 2;
    if (minutes <= 60) return 3;
    if (minutes <= 90) return 4;
    return 5;
  };

  const formatMinutes = (minutes: number): string => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  if (loading) {
    return (
      <div className={`rounded-2xl p-6 ${
        isDarkMode ? 'bg-slate-900/50 border border-slate-700/50' : 'bg-white border border-gray-200'
      }`}>
        <div className="animate-pulse">
          <div className={`h-6 w-40 rounded mb-4 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`} />
          <div className={`h-32 rounded ${isDarkMode ? 'bg-slate-800' : 'bg-gray-100'}`} />
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl p-6 ${
      isDarkMode ? 'bg-slate-900/50 border border-slate-700/50' : 'bg-white border border-gray-200'
    }`}>
      <h2 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
        Reading Activity
      </h2>

      <div className="overflow-x-auto">
        <div className="min-w-[750px]">
          <CalendarHeatmap
            startDate={startDate}
            endDate={endDate}
            values={activity.map(a => ({ date: a.date, count: a.minutes }))}
            classForValue={(value) => {
              if (!value || value.count === 0) {
                return `color-empty ${isDarkMode ? 'dark' : 'light'}`;
              }
              return `color-scale-${getColorLevel(value.count)} ${isDarkMode ? 'dark' : 'light'}`;
            }}
            tooltipDataAttrs={(value: any) => {
              if (!value || !value.date) {
                return { 'data-tooltip-id': 'heatmap-tooltip', 'data-tooltip-content': 'No reading' } as any;
              }
              const minutes = activityMap.get(value.date) || 0;
              // Parse as local date (not UTC) to avoid timezone shift
              const date = new Date(value.date + 'T00:00:00').toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              });
              return {
                'data-tooltip-id': 'heatmap-tooltip',
                'data-tooltip-content': minutes > 0
                  ? `${formatMinutes(minutes)} on ${date}`
                  : `No reading on ${date}`
              } as any;
            }}
            showWeekdayLabels
            gutterSize={3}
          />
        </div>
      </div>

      <Tooltip id="heatmap-tooltip" />

      {/* Legend */}
      <div className="flex items-center justify-end mt-4 gap-2">
        <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Less</span>
        <div className="flex gap-1">
          {[0, 1, 2, 3, 4, 5].map((level) => (
            <div
              key={level}
              className={`w-3 h-3 rounded-sm heatmap-legend-${level} ${isDarkMode ? 'dark' : 'light'}`}
            />
          ))}
        </div>
        <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>More</span>
      </div>

      {/* Custom styles */}
      <style>{`
        .react-calendar-heatmap text {
          font-size: 10px;
          fill: ${isDarkMode ? '#64748b' : '#6b7280'};
        }

        .react-calendar-heatmap .color-empty.dark {
          fill: #1e293b;
        }
        .react-calendar-heatmap .color-empty.light {
          fill: #f1f5f9;
        }

        .react-calendar-heatmap .color-scale-1.dark {
          fill: #134e4a;
        }
        .react-calendar-heatmap .color-scale-1.light {
          fill: #99f6e4;
        }

        .react-calendar-heatmap .color-scale-2.dark {
          fill: #0f766e;
        }
        .react-calendar-heatmap .color-scale-2.light {
          fill: #2dd4bf;
        }

        .react-calendar-heatmap .color-scale-3.dark {
          fill: #14b8a6;
        }
        .react-calendar-heatmap .color-scale-3.light {
          fill: #0d9488;
        }

        .react-calendar-heatmap .color-scale-4.dark {
          fill: #2dd4bf;
        }
        .react-calendar-heatmap .color-scale-4.light {
          fill: #0f766e;
        }

        .react-calendar-heatmap .color-scale-5.dark {
          fill: #5eead4;
        }
        .react-calendar-heatmap .color-scale-5.light {
          fill: #115e59;
        }

        .heatmap-legend-0.dark { background-color: #1e293b; }
        .heatmap-legend-0.light { background-color: #f1f5f9; }
        .heatmap-legend-1.dark { background-color: #134e4a; }
        .heatmap-legend-1.light { background-color: #99f6e4; }
        .heatmap-legend-2.dark { background-color: #0f766e; }
        .heatmap-legend-2.light { background-color: #2dd4bf; }
        .heatmap-legend-3.dark { background-color: #14b8a6; }
        .heatmap-legend-3.light { background-color: #0d9488; }
        .heatmap-legend-4.dark { background-color: #2dd4bf; }
        .heatmap-legend-4.light { background-color: #0f766e; }
        .heatmap-legend-5.dark { background-color: #5eead4; }
        .heatmap-legend-5.light { background-color: #115e59; }
      `}</style>
    </div>
  );
}
